/**
 * Mo ta loai cuoc lo de cho GIAO DIEN.
 *
 * Day la ban PHAN CHIEU cua `src/modules/lode/bet-types.ts`, khong phai nguon su that.
 * May chu van kiem lai toan bo (`assertAtomicPick` goi ngay trong `evaluate`), nen neu
 * hai ben lech nhau thi may chu thang va nguoi choi nhan mot thong bao tu choi. Bang
 * nay chi de giao dien bao truoc "loai nay chon may so, may chu so" thay vi de nguoi
 * choi go xong roi moi biet sai.
 *
 * Khong sinh tu API vi day la LUAT CHOI co dinh trong ma, khong phai cau hinh cua nha
 * khai thac - nha khai thac chi bat/tat loai cuoc theo mien (LDE-R02).
 */

import type { LodeRegion } from './lode-api';

export interface LodeBetTypeMeta {
  /** Ten hien thi tieng Viet. */
  label: string;
  /** So luong so phai chon. */
  picks: number;
  /** So chu so cua moi so. */
  digits: number;
  /** Mien ban duoc. */
  regions: readonly LodeRegion[];
  /** Chon nhieu so thi khong duoc trung nhau (XIEN/DA). */
  distinct?: boolean;
  /** Tra thuong theo SO NHAY chu khong phai 0/1 - can noi ro cho nguoi choi. */
  perHit?: boolean;
  /** Dang nhap gop theo DAI: dung `draw_ids`. */
  multiDraw?: boolean;
  /** Dang nhap gop tach thanh nhieu dong: moi dong tinh tien rieng. */
  expandsTo?: string;
  hint?: string;
}

const ALL: readonly LodeRegion[] = ['MB', 'MT', 'MN'];
const MB: readonly LodeRegion[] = ['MB'];
const MT_MN: readonly LodeRegion[] = ['MT', 'MN'];

export const LODE_BET_TYPES: Readonly<Record<string, LodeBetTypeMeta>> = {
  DE: { label: 'Đề', picks: 1, digits: 2, regions: ALL, hint: 'Hai số cuối giải Đặc biệt.' },
  DE_G1: { label: 'Đề giải Nhất', picks: 1, digits: 2, regions: ALL, hint: 'Hai số cuối giải Nhất.' },
  DAU: { label: 'Đầu', picks: 1, digits: 2, regions: ALL, hint: 'Giải Bảy (Bắc) hoặc giải Tám (Trung, Nam).' },
  DUOI: { label: 'Đuôi', picks: 1, digits: 2, regions: ALL, hint: 'Hai số cuối giải Đặc biệt.' },
  LO2: { label: 'Lô 2 số', picks: 1, digits: 2, regions: ALL, perHit: true, hint: 'Trả theo số nháy trên toàn bảng.' },
  LO3: { label: 'Lô 3 số', picks: 1, digits: 3, regions: ALL, perHit: true },
  LO4: { label: 'Lô 4 số', picks: 1, digits: 4, regions: ALL, perHit: true },
  XIEN2: { label: 'Xiên 2', picks: 2, digits: 2, regions: MB, distinct: true, hint: 'Cả hai số đều phải về.' },
  XIEN3: { label: 'Xiên 3', picks: 3, digits: 2, regions: MB, distinct: true, hint: 'Cả ba số đều phải về.' },
  XIEN4: { label: 'Xiên 4', picks: 4, digits: 2, regions: MB, distinct: true, hint: 'Cả bốn số đều phải về.' },
  DA2: { label: 'Đá 2', picks: 2, digits: 2, regions: MT_MN, distinct: true, hint: 'Cả hai số về; nháy càng nhiều bậc trả càng cao.' },
  DA3: { label: 'Đá 3', picks: 3, digits: 2, regions: MT_MN, distinct: true },
  DA4: { label: 'Đá 4', picks: 4, digits: 2, regions: MT_MN, distinct: true },
  CANG3: { label: 'Càng 3', picks: 1, digits: 3, regions: MB },
  CANG4: { label: 'Càng 4', picks: 1, digits: 4, regions: MB },
  XIU_CHU_DAU: { label: 'Xỉu chủ đầu', picks: 1, digits: 3, regions: MT_MN },
  XIU_CHU_DUOI: { label: 'Xỉu chủ đuôi', picks: 1, digits: 3, regions: MT_MN },

  DAU_DUOI: {
    label: 'Đầu đuôi', picks: 1, digits: 2, regions: ALL,
    expandsTo: 'hai dòng: Đầu và Đuôi', hint: 'Một số, tính tiền hai dòng.',
  },
  LO_KEP: {
    label: 'Lô kép', picks: 0, digits: 2, regions: ALL, perHit: true,
    expandsTo: 'mười dòng Lô 2 số (00, 11, … 99)', hint: 'Tập số cố định, không nhập số.',
  },
  DAN_DE: {
    label: 'Dàn đề', picks: -1, digits: 2, regions: ALL,
    expandsTo: 'mỗi số một dòng Đề', hint: 'Chọn 1–100 số, mỗi số tính tiền riêng.',
  },
  BAO_LO: {
    label: 'Bao lô', picks: 1, digits: 2, regions: MT_MN, multiDraw: true, perHit: true,
    expandsTo: 'mỗi đài một dòng Lô 2 số', hint: 'Một số, đánh trên 2–10 đài; mỗi đài tính tiền riêng.',
  },
};

export const DAN_DE_MAX_PICKS = 100;
export const BAO_LO_MIN_DRAWS = 2;
export const BAO_LO_MAX_DRAWS = 10;

export function betTypeLabel(code: string): string {
  return LODE_BET_TYPES[code]?.label ?? code;
}

export function betTypesForRegion(region: LodeRegion): string[] {
  return Object.entries(LODE_BET_TYPES)
    .filter(([, meta]) => meta.regions.includes(region))
    .map(([code]) => code);
}

/**
 * Kiem tap so o phia giao dien de bao loi NGAY khi go, khong doi may chu tra loi.
 * Tra ve cau chu tieng Viet, hoac `null` khi hop le.
 *
 * Ban chinh thuc cua phep kiem nay nam o may chu; day chi la lop bao truoc.
 */
export function checkPicks(code: string, numbers: number[]): string | null {
  const meta = LODE_BET_TYPES[code];
  if (!meta) return null;

  const limit = 10 ** meta.digits;
  for (const n of numbers) {
    if (!Number.isInteger(n) || n < 0 || n >= limit) {
      return `${meta.label} chỉ nhận số ${meta.digits} chữ số (0–${limit - 1}).`;
    }
  }

  if (code === 'DAN_DE') {
    if (numbers.length < 1) return 'Dàn đề phải chọn ít nhất một số.';
    if (numbers.length > DAN_DE_MAX_PICKS) return `Dàn đề tối đa ${DAN_DE_MAX_PICKS} số.`;
  } else if (meta.picks >= 0 && numbers.length !== meta.picks) {
    return meta.picks === 0
      ? `${meta.label} không nhận danh sách số.`
      : `${meta.label} phải chọn đúng ${meta.picks} số, đang có ${numbers.length}.`;
  }

  if ((meta.distinct || code === 'DAN_DE') && new Set(numbers).size !== numbers.length) {
    return `${meta.label} không được chọn trùng số.`;
  }
  return null;
}
